import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { FaFacebook, FaWhatsapp } from 'react-icons/fa';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500&display=swap');

  :root {
    --teal-top:     #00adb5;
    --teal-nav:     #008080;
    --teal-nav-h:   #006e6e;
    --accent:       #ff5722;
    --accent-h:     #e64a19;
    --white:        #ffffff;
    --white-muted:  rgba(255,255,255,0.72);
    --transition:   0.22s cubic-bezier(0.4,0,0.2,1);
  }

  .vedo-header {
    position: sticky;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 9999;
    font-family: 'DM Sans', sans-serif;
  }

  /* ── Faixa 1: Logo ── */
  .vedo-top {
    background: var(--teal-top);
    padding: 0 24px;
    height: 52px;
    display: flex;
    align-items: center;
    box-shadow: inset 0 -1px 0 rgba(0,0,0,0.08);
  }

  .vedo-logo {
    display: flex;
    align-items: center;
    text-decoration: none;
  }
  .vedo-logo img {
    height: 48px;
    width: auto;
    object-fit: contain;
    display: block;
    transition: opacity var(--transition), transform var(--transition);
  }
  .vedo-logo:hover img {
    opacity: 0.88;
    transform: scale(1.02);
  }

  /* ── Faixa 2: Navbar ── */
  .vedo-nav {
    background: var(--teal-nav);
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 0 20px;
    gap: 2px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.18);
  }

  /* Social */
  .vedo-nav__social {
    display: flex;
    align-items: center;
    gap: 2px;
    margin-right: 8px;
    padding-right: 12px;
    border-right: 1px solid rgba(255,255,255,0.2);
  }
  .vedo-nav__social-link {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    color: var(--white);
    font-size: 20px;
    transition: background var(--transition), transform var(--transition);
  }
  .vedo-nav__social-link:hover {
    background: rgba(255,255,255,0.12);
    transform: scale(1.1);
  }

  /* Links */
  .vedo-nav__link {
    text-decoration: none;
    color: var(--white-muted);
    font-size: 14px;
    font-weight: 400;
    padding: 6px 14px;
    border-radius: 5px;
    letter-spacing: 0.02em;
    transition: color var(--transition), background var(--transition);
    position: relative;
  }
  .vedo-nav__link::after {
    content: '';
    position: absolute;
    bottom: 3px;
    left: 14px;
    right: 14px;
    height: 1.5px;
    background: rgba(255,255,255,0.7);
    border-radius: 2px;
    transform: scaleX(0);
    transition: transform var(--transition);
  }
  .vedo-nav__link:hover { color: var(--white); background: rgba(255,255,255,0.08); }
  .vedo-nav__link:hover::after { transform: scaleX(1); }

  /* CTA */
  .vedo-nav__cta {
    text-decoration: none;
    color: var(--white);
    background: var(--accent);
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.02em;
    padding: 8px 20px;
    border-radius: 5px;
    margin-left: 6px;
    box-shadow: 0 2px 10px rgba(255,87,34,0.4);
    transition: background var(--transition), box-shadow var(--transition), transform var(--transition);
    white-space: nowrap;
    cursor: pointer;
    border: none;
    font-family: 'DM Sans', sans-serif;
  }
  .vedo-nav__cta:hover {
    background: var(--accent-h);
    box-shadow: 0 4px 16px rgba(255,87,34,0.55);
    transform: translateY(-1px);
  }
  .vedo-nav__cta:active { transform: translateY(0); }

  /* ── Avatar do usuário ── */
  .vedo-nav__user {
    position: relative;
    margin-left: 10px;
    padding-left: 10px;
    border-left: 1px solid rgba(255,255,255,0.2);
  }

  .vedo-nav__avatar-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 3px;
    border-radius: 30px;
    transition: background var(--transition);
  }
  .vedo-nav__avatar-btn:hover { background: rgba(255,255,255,0.1); }

  .vedo-nav__avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 2px solid rgba(255,255,255,0.35);
    object-fit: cover;
    display: block;
    transition: border-color var(--transition);
  }
  .vedo-nav__avatar-btn:hover .vedo-nav__avatar { border-color: rgba(255,255,255,0.7); }

  /* fallback inicial */
  .vedo-nav__avatar-initial {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 2px solid rgba(255,255,255,0.35);
    background: rgba(0,0,0,0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 600;
    color: var(--white);
    transition: border-color var(--transition);
    flex-shrink: 0;
  }
  .vedo-nav__avatar-btn:hover .vedo-nav__avatar-initial { border-color: rgba(255,255,255,0.7); }

  .vedo-nav__avatar-name {
    font-size: 13px;
    font-weight: 500;
    color: var(--white-muted);
    max-width: 110px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Dropdown */
  .vedo-nav__dropdown {
    position: absolute;
    top: calc(100% + 10px);
    right: 0;
    background: #0f1e35;
    border: 1px solid rgba(0,173,181,0.22);
    border-radius: 10px;
    padding: 6px;
    min-width: 200px;
    box-shadow: 0 12px 32px rgba(0,0,0,0.5);
    animation: dropIn 0.18s cubic-bezier(0.4,0,0.2,1);
    z-index: 100;
  }
  @keyframes dropIn {
    from { opacity: 0; transform: translateY(-6px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .vedo-nav__dropdown-header {
    padding: 10px 12px 12px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    margin-bottom: 6px;
  }
  .vedo-nav__dropdown-name {
    font-size: 13.5px;
    font-weight: 600;
    color: #f0f4f8;
    margin-bottom: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .vedo-nav__dropdown-email {
    font-size: 11.5px;
    color: rgba(240,244,248,0.45);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .vedo-nav__dropdown-item {
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    background: none;
    border: none;
    border-radius: 6px;
    padding: 9px 12px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    color: rgba(240,244,248,0.6);
    cursor: pointer;
    transition: background var(--transition), color var(--transition);
    text-align: left;
  }
  .vedo-nav__dropdown-item:hover {
    background: rgba(255,255,255,0.06);
    color: #f0f4f8;
  }
  .vedo-nav__dropdown-item--danger:hover {
    background: rgba(255,87,34,0.1);
    color: #ff7043;
  }

  /* ── Hamburger ── */
  .vedo-hamburger {
    display: none;
    flex-direction: column;
    gap: 5px;
    cursor: pointer;
    padding: 6px;
    background: none;
    border: none;
  }
  .vedo-hamburger span {
    display: block;
    width: 22px;
    height: 2px;
    background: var(--white);
    border-radius: 2px;
    transition: transform var(--transition), opacity var(--transition);
    transform-origin: center;
  }
  .vedo-hamburger.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
  .vedo-hamburger.open span:nth-child(2) { opacity: 0; }
  .vedo-hamburger.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

  /* ── Drawer mobile ── */
  .vedo-drawer {
    display: none;
    position: fixed;
    top: 100px;
    left: 0; right: 0; bottom: 0;
    background: var(--teal-nav);
    padding: 16px 20px 32px;
    flex-direction: column;
    gap: 4px;
    z-index: 9998;
    transform: translateY(-6px);
    opacity: 0;
    pointer-events: none;
    transition: opacity var(--transition), transform var(--transition);
  }
  .vedo-drawer.open {
    opacity: 1;
    transform: translateY(0);
    pointer-events: all;
  }
  .vedo-drawer__link {
    text-decoration: none;
    color: var(--white-muted);
    font-size: 15px;
    padding: 13px 16px;
    border-radius: 6px;
    border: 1px solid transparent;
    transition: color var(--transition), border-color var(--transition), background var(--transition);
  }
  .vedo-drawer__link:hover {
    color: var(--white);
    border-color: rgba(255,255,255,0.18);
    background: rgba(255,255,255,0.07);
  }
  .vedo-drawer__divider {
    height: 1px;
    background: rgba(255,255,255,0.12);
    margin: 6px 0;
  }
  .vedo-drawer__cta {
    text-decoration: none;
    color: var(--white);
    background: var(--accent);
    font-size: 15px;
    font-weight: 600;
    text-align: center;
    padding: 14px 20px;
    border-radius: 6px;
    margin-top: 4px;
    box-shadow: 0 3px 14px rgba(255,87,34,0.4);
    transition: background var(--transition), box-shadow var(--transition);
    cursor: pointer;
    border: none;
    font-family: 'DM Sans', sans-serif;
    display: block;
  }
  .vedo-drawer__cta:hover {
    background: var(--accent-h);
    box-shadow: 0 5px 20px rgba(255,87,34,0.55);
  }
  .vedo-drawer__social {
    display: flex;
    gap: 10px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid rgba(255,255,255,0.1);
  }
  .vedo-drawer__social-link {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-decoration: none;
    color: var(--white-muted);
    font-size: 14px;
    padding: 11px 12px;
    border-radius: 6px;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.1);
    transition: color var(--transition), background var(--transition), border-color var(--transition);
  }
  .vedo-drawer__social-link--fb:hover { color: #1877f2; background: rgba(24,119,242,0.1); border-color: rgba(24,119,242,0.3); }
  .vedo-drawer__social-link--wa:hover { color: #25d366; background: rgba(37,211,102,0.1); border-color: rgba(37,211,102,0.3); }

  /* Usuário no drawer mobile */
  .vedo-drawer__user {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    background: rgba(255,255,255,0.06);
    border-radius: 8px;
    margin-bottom: 4px;
  }
  .vedo-drawer__user-info { flex: 1; min-width: 0; }
  .vedo-drawer__user-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--white);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .vedo-drawer__user-email {
    font-size: 11px;
    color: var(--white-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .vedo-drawer__logout {
    background: none;
    border: 1px solid rgba(255,87,34,0.4);
    border-radius: 6px;
    color: #ff7043;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    padding: 5px 10px;
    cursor: pointer;
    transition: background var(--transition);
    flex-shrink: 0;
  }
  .vedo-drawer__logout:hover { background: rgba(255,87,34,0.1); }

  /* ── Mobile breakpoint ── */
  @media (max-width: 768px) {
    .vedo-nav__social  { display: none; }
    .vedo-nav__link    { display: none; }
    .vedo-nav__cta     { display: none; }
    .vedo-nav__user    { display: none; }
    .vedo-hamburger    { display: flex; }
    .vedo-drawer       { display: flex; }
  }

  /* ──────────────────────────────
   Sidebar do usuário
  ────────────────────────────── */

  .vedo-sidebar-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,.45);
    opacity: 0;
    visibility: hidden;
    transition: .25s;
    z-index: 10000;
  }

  .vedo-sidebar-overlay.open {
    opacity: 1;
    visibility: visible;
  }

  .vedo-sidebar {
    position: fixed;
    top: 0;
    right: 0;
    width: 420px;
    max-width: 90vw;
    height: 100vh;
    height: 100dvh;

    background: #0f1e35;

    box-shadow: -8px 0 30px rgba(0,0,0,.35);

    transform: translateX(100%);
    transition: transform .25s ease;

    z-index: 10001;

    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .vedo-sidebar.open {
    transform: translateX(0);
  }

  .vedo-sidebar__header {
    position: relative;
    flex-shrink: 0;
    padding: 28px 56px 24px 24px;
    border-bottom: 1px solid rgba(255,255,255,.08);

    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .vedo-sidebar__avatar {
    margin-bottom: 14px;
  }

  .vedo-sidebar__name {
    color: #fff;
    font-size: 18px;
    font-weight: 600;
  }

  .vedo-sidebar__email {
    color: rgba(255,255,255,.55);
    font-size: 13px;
    margin-top: 4px;
    word-break: break-all;
  }

  .vedo-sidebar__content {
    flex: 1 1 auto;
    min-height: 0;
    padding: 20px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .vedo-sidebar__link {
    display: block;
    text-decoration: none;
    color: rgba(255,255,255,.8);
    padding: 14px 18px;
    border-radius: 10px;
    margin-bottom: 8px;
    transition: all .2s ease;
  }

  .vedo-sidebar__link:hover {
    background: rgba(255,255,255,.06);
    color: #fff;
    transform: translateX(4px);
  }

  .vedo-sidebar__footer {
    flex-shrink: 0;
    padding: 20px;
    border-top: 1px solid rgba(255,255,255,.08);
  }

  .vedo-sidebar__logout {
    width: 100%;
    border: none;
    border-radius: 8px;
    background: rgba(255,87,34,.15);
    color: #ff7043;
    padding: 14px;
    cursor: pointer;
    font-weight: 600;
  }

  .vedo-sidebar__logout:hover {
    background: rgba(255,87,34,.25);
  }

  .vedo-sidebar__close {
    position: absolute;
    top: 18px;
    right: 18px;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: rgba(255,255,255,.08);
    color: white;
    cursor: pointer;
    transition: .2s;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
  }

  .vedo-sidebar__close:hover {
    background: rgba(255,255,255,.16);
  }

  @media (max-width: 480px) {
    .vedo-sidebar {
      width: 100vw;
      max-width: 100vw;
    }
  }

  /* Botão "Entrar" - Estilo Suave (Ghost/Outline) */
  .btn-entrar-suave {
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: #fff;
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-right: 15px;
    font-family: 'DM Sans', sans-serif;
  }

  .btn-entrar-suave:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: #fff;
  }
`;

function Header({ onLoginClick }) {
  const [menuOpen, setMenuOpen]     = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser]               = useState(null);
  const dropdownRef                   = useRef(null);
  const auth                          = getAuth();
  const navigate                      = useNavigate();

  // Observa auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u ?? null));
    return () => unsub();
  }, []);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        // dropdown fechado via ref
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSignOut = () => {
    signOut(auth);
    setSidebarOpen(false);
    setMenuOpen(false);
  };

  const closeMenu = () => setMenuOpen(false);

  // Botão "Anunciar Domínio": abre login se não logado, navega se logado
  const handleAnunciarClick = () => {
    if (user) {
      navigate('/Anunciar');
    } else {
      onLoginClick();
    }
  };

  // Botão "Anunciar" no drawer mobile
  const handleAnunciarDrawerClick = () => {
    closeMenu();
    if (user) {
      navigate('/Anunciar');
    } else {
      onLoginClick();
    }
  };

  useEffect(() => {
    document.body.style.overflow = menuOpen || sidebarOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen, sidebarOpen]);

  // Avatar: foto Google se disponível, senão inicial
  const AvatarImg = ({ size = 32 }) => {
    if (user?.photoURL) {
      return (
        <img
          src={user.photoURL}
          alt={user.displayName || 'Avatar'}
          className="vedo-nav__avatar"
          style={{ width: size, height: size }}
          referrerPolicy="no-referrer"
        />
      );
    }
    const initial = (user?.displayName || user?.email || '?')[0].toUpperCase();
    return (
      <div
        className="vedo-nav__avatar-initial"
        style={{ width: size, height: size, fontSize: size * 0.4 }}
      >
        {initial}
      </div>
    );
  };

  const displayName = user?.displayName || user?.email?.split('@')[0] || 'Usuário';

  return (
    <>
      <style>{styles}</style>

      <header className="vedo-header">

        {/* ── Faixa 1: Logo ── */}
        <div className="vedo-top">
          <a href="https://www.vedo.com.br/" className="vedo-logo" rel="noopener noreferrer">
            <img src="/logov.png" alt="VEDO" />
          </a>
        </div>

        {/* ── Faixa 2: Navbar ── */}
        <nav className="vedo-nav" aria-label="Navegação principal">

          <div className="vedo-nav__social">
            <a href="https://www.facebook.com/dominios.vedo" target="_blank" rel="noopener noreferrer"
               className="vedo-nav__social-link" aria-label="Facebook">
              <FaFacebook />
            </a>
            <a href="https://wa.me/551150502769" target="_blank" rel="noopener noreferrer"
               className="vedo-nav__social-link" aria-label="WhatsApp">
              <FaWhatsapp />
            </a>
          </div>

          <Link to="/" className="vedo-nav__link">Home</Link>
          <Link to="/QuemSomos" className="vedo-nav__link">Quem Somos?</Link>

          {/* Botão "Entrar" — só exibido quando não logado */}
          {!user && (
            <button onClick={onLoginClick} className="btn-entrar-suave">
              Entrar
            </button>
          )}

          {/*
            "Anunciar Domínio":
            - Não logado → abre modal de login (onLoginClick)
            - Logado     → navega para /Anunciar
          */}
          <button onClick={handleAnunciarClick} className="vedo-nav__cta">
            Anunciar Domínio
          </button>

          {/* Avatar — só se logado */}
          {user && (
            <div className="vedo-nav__user" ref={dropdownRef}>
              <button
                className="vedo-nav__avatar-btn"
                onClick={() => setSidebarOpen(true)}
                aria-label="Menu do usuário"
              >
                <AvatarImg size={32} />
                <span className="vedo-nav__avatar-name">{displayName}</span>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ opacity: 0.5, flexShrink: 0 }}>
                  <path d="M2 3.5L5 6.5L8 3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          )}

          {/* Hamburger mobile */}
          <button
            className={`vedo-hamburger${menuOpen ? ' open' : ''}`}
            onClick={() => setMenuOpen(v => !v)}
            aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={menuOpen}
          >
            <span /><span /><span />
          </button>
        </nav>
      </header>

      {/* ── Drawer mobile ── */}
      <nav className={`vedo-drawer${menuOpen ? ' open' : ''}`} aria-label="Menu mobile">

        {/* Usuário logado no drawer */}
        {user && (
          <div className="vedo-drawer__user">
            <AvatarImg size={36} />
            <div className="vedo-drawer__user-info">
              <div className="vedo-drawer__user-name">{displayName}</div>
              <div className="vedo-drawer__user-email">{user.email}</div>
            </div>
            <button className="vedo-drawer__logout" onClick={handleSignOut}>Sair</button>
          </div>
        )}

        <Link to="/" className="vedo-drawer__link" onClick={closeMenu}>Home</Link>
        <Link to="/QuemSomos" className="vedo-drawer__link" onClick={closeMenu}>Quem Somos?</Link>
        <div className="vedo-drawer__divider" />

        {/*
          "Anunciar Domínio" no drawer mobile:
          - Não logado → fecha menu e abre login
          - Logado     → fecha menu e navega para /Anunciar
        */}
        <button onClick={handleAnunciarDrawerClick} className="vedo-drawer__cta">
          Anunciar Domínio
        </button>

        <div className="vedo-drawer__social">
          <a href="https://www.facebook.com/dominios.vedo" target="_blank" rel="noopener noreferrer"
             className="vedo-drawer__social-link vedo-drawer__social-link--fb">
            <FaFacebook size={17} /> Facebook
          </a>
          <a href="https://wa.me/551150502769" target="_blank" rel="noopener noreferrer"
             className="vedo-drawer__social-link vedo-drawer__social-link--wa">
            <FaWhatsapp size={17} /> WhatsApp
          </a>
        </div>
      </nav>

      {/* ── Sidebar do usuário (via portal) ── */}
      {user && createPortal(
        <>
          <div
            className={`vedo-sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
            onClick={() => setSidebarOpen(false)}
            style={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,.45)',
              opacity: sidebarOpen ? 1 : 0,
              visibility: sidebarOpen ? 'visible' : 'hidden',
              transition: '.25s',
              zIndex: 10000,
            }}
          />

          <aside
            className={`vedo-sidebar ${sidebarOpen ? 'open' : ''}`}
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              width: '420px',
              maxWidth: '90vw',
              height: '100vh',
              background: '#0f1e35',
              boxShadow: '-8px 0 30px rgba(0,0,0,.35)',
              transform: sidebarOpen ? 'translateX(0)' : 'translateX(100%)',
              transition: 'transform .25s ease',
              zIndex: 10001,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div className="vedo-sidebar__header" style={{ position: 'relative', flexShrink: 0, padding: '28px 56px 24px 24px', borderBottom: '1px solid rgba(255,255,255,.08)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <button
                className="vedo-sidebar__close"
                onClick={() => setSidebarOpen(false)}
                aria-label="Fechar menu"
              >
                ✕
              </button>

              <div className="vedo-sidebar__avatar">
                <AvatarImg size={72} />
              </div>

              <div className="vedo-sidebar__name">
                {displayName}
              </div>

              <div className="vedo-sidebar__email">
                {user.email}
              </div>
            </div>

            <div className="vedo-sidebar__content" style={{ flex: '1 1 auto', minHeight: 0, padding: '20px', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>

              <Link
                to="/minha-conta"
                className="vedo-sidebar__link"
                onClick={() => setSidebarOpen(false)}
              >
                Minha Conta
              </Link>

              <Link
                to="/meusanuncios"
                className="vedo-sidebar__link"
                onClick={() => setSidebarOpen(false)}
              >
                Meus Domínios
              </Link>

              <Link
                to="/favoritos"
                className="vedo-sidebar__link"
                onClick={() => setSidebarOpen(false)}
              >
                Favoritos
              </Link>

              <Link
                to="/configuracoes"
                className="vedo-sidebar__link"
                onClick={() => setSidebarOpen(false)}
              >
                Configurações
              </Link>

            </div>

            <div className="vedo-sidebar__footer" style={{ flexShrink: 0, padding: '20px', borderTop: '1px solid rgba(255,255,255,.08)' }}>
              <button
                className="vedo-sidebar__logout"
                onClick={handleSignOut}
              >
                Sair da Conta
              </button>
            </div>
          </aside>
        </>,
        document.body
      )}
    </>
  );
}

export default Header;
